import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import { usePageMeta } from '@/hooks/usePageMeta';
import ContactForm from './components/ContactForm';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只做整理，不做爆料',
    desc: '站内内容来自公开讨论的归纳与盘点，不发布独家爆料，也不使用无法核对的来源。',
  },
  {
    icon: 'ri-lock-2-line',
    title: '隐私话题一律不碰',
    desc: '涉及个人隐私、家庭信息、未经证实的指控类内容，即使讨论量再高也不收录。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '把不同声音摆出来',
    desc: '盘点文章会尽量呈现多方观点，避免只给一种情绪，把判断权留给读者。',
  },
  {
    icon: 'ri-refresh-line',
    title: '发现错误立即更正',
    desc: '收到可核对的纠错信息后，我们会尽快修订并标注更新，不做掩盖处理。',
  },
];

const testimonials = [
  {
    name: '每天通勤刷一刷',
    role: '上班族读者',
    content: '以前要在几个平台来回跳，现在一页就能把当天的事扫完，通勤路上刚好够看。',
  },
  {
    name: '柠檬气泡',
    role: '长期读者',
    content: '最舒服的是它不卖惨也不煽动，就是把来龙去脉讲清楚，还有不同声音，省心。',
  },
  {
    name: '追剧到天明',
    role: '剧集观众',
    content: '分类做得好，我只关心影视和音乐，点一下标签就只剩我想看的，很省时间。',
  },
  {
    name: '理性吃瓜',
    role: '内容从业者',
    content: '这个站的分寸感不错，不碰隐私不造谣，作为娱乐资讯参考还是挺靠谱的。',
  },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于黑料今日',
        description: '黑料今日的品牌介绍、网站定位、内容原则与联系方式。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '黑料今日',
        description: '面向简体中文用户的娱乐热点聚合与热搜盘点内容站。',
        email: 'contact@heiliao-today.example',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-today.example/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://heiliao-today.example/about' },
        ],
      },
    ],
    []
  );

  usePageMeta({
    title: '关于我们 - 黑料今日的定位、内容原则与联系方式',
    description:
      '了解黑料今日的品牌定位、内容原则与团队做事方式：只整理公开讨论、不碰隐私话题、呈现多方声音，并提供内容纠错与订阅每日热点的联系方式。',
    keywords: '关于黑料今日,内容原则,娱乐热点聚合,热搜盘点,联系方式',
    path: '/about',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteNavbar />

      <main className="flex-1 pt-16">
        <section className="w-full bg-secondary-950">
          <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-secondary-300/80">
                <li>
                  <Link to="/" className="hover:text-accent-300 transition-colors">
                    首页
                  </Link>
                </li>
                <li aria-hidden="true">
                  <i className="ri-arrow-right-s-line"></i>
                </li>
                <li className="text-accent-300">关于我们</li>
              </ol>
            </nav>
            <p className="flex items-center gap-2 text-xs tracking-[0.24em] text-accent-400 uppercase mb-4">
              <span className="w-6 h-px bg-accent-500" aria-hidden="true"></span>
              About Us
            </p>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 max-w-3xl leading-snug">
              我们只做一件事：把今天的热点，讲清楚
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-100/75 leading-7 max-w-3xl">
              黑料今日是一个娱乐热点聚合与热搜盘点内容站。我们希望把讨论量高的话题按门类收拢，
              用一页读完的篇幅交代来龙去脉，让你用最少的时间知道今天发生了什么。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">品牌介绍</h2>
                <p className="mt-5 text-base text-foreground-700 leading-8">
                  「黑料今日」这个名字有点直白，但它说的其实是「今天大家都在聊什么」。我们不做审判者，
                  也不追求爆料速度，更愿意当一个把信息收拢整齐的整理者：同一件事，标题、来龙去脉、
                  不同声音、讨论热度，都摆在同一个页面里。
                </p>
                <p className="mt-4 text-base text-foreground-700 leading-8">
                  站点每天上午完成第一轮更新，晚间根据讨论量做一次热度校准。榜单的排序依据是公开可观察的
                  讨论数据，它不是结论，只是一张「今天哪里在热闹」的地图。
                </p>
              </div>

              <div className="bg-background-100 border border-background-200 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold text-foreground-950">网站定位</h2>
                <ul className="mt-5 space-y-4">
                  <li className="flex gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className="ri-focus-3-line" aria-hidden="true"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-7">
                      <strong className="text-foreground-950">聚焦娱乐领域</strong>，覆盖影视、音乐、综艺、品牌
                      与粉丝圈层五类话题，不做泛资讯堆砌。
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center">
                      <i className="ri-speed-up-line" aria-hidden="true"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-7">
                      <strong className="text-foreground-950">服务「快速扫一眼」</strong>，榜单和短盘点优先，
                      深度内容按需点开，不强迫长阅读。
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                      <i className="ri-group-line" aria-hidden="true"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-7">
                      <strong className="text-foreground-950">面向普通观众</strong>，不用行业黑话，
                      不预设你有追星背景，谁看都能看懂。
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">内容原则</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-7 max-w-2xl">
              这四条不是口号，是我们每天筛内容时实际在用的判断标准。少一条都宁愿不发。
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {principles.map((item, index) => (
                <article
                  key={item.title}
                  className="bg-background-50 border border-background-200 rounded-lg p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="w-11 h-11 rounded-md bg-secondary-950 text-accent-400 flex items-center justify-center text-xl">
                      <i className={item.icon} aria-hidden="true"></i>
                    </span>
                    <span className="font-heading text-2xl font-black text-background-300">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-3 text-sm text-foreground-600 leading-7">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">用户怎么说</h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-7 max-w-2xl">
              来自留言和邮件里的一些反馈，原样呈现，不做修饰。
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {testimonials.map((item) => (
                <figure
                  key={item.name}
                  className="bg-background-100 border border-background-200 rounded-lg p-6 flex flex-col"
                >
                  <span className="w-8 h-8 flex items-center justify-center text-accent-500 text-xl">
                    <i className="ri-double-quotes-l" aria-hidden="true"></i>
                  </span>
                  <blockquote className="mt-3 text-sm text-foreground-700 leading-7 flex-1">{item.content}</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-background-200">
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-14">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">联系方式</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-7">
                  内容纠错、话题投稿、合作咨询都可以直接联系。我们更看重可核对的来源，写清楚会处理得更快。
                </p>
                <ul className="mt-8 space-y-5">
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-600">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm text-foreground-500">联系邮箱</span>
                      <span className="block text-sm font-medium text-foreground-900">contact@heiliao-today.example</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-600">
                      <i className="ri-time-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm text-foreground-500">回复时间</span>
                      <span className="block text-sm font-medium text-foreground-900">工作日 10:00 - 18:00</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-600">
                      <i className="ri-shield-star-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm text-foreground-500">处理优先级</span>
                      <span className="block text-sm font-medium text-foreground-900">隐私类纠错优先于其他留言</span>
                    </span>
                  </li>
                </ul>

                <div className="mt-8 p-5 rounded-lg bg-accent-50 border border-accent-200">
                  <p className="text-xs text-foreground-700 leading-6">
                    站内所有内容为公开网络讨论的整理与盘点，仅代表网友观点，不构成事实认定，也不代表站方立场。
                  </p>
                </div>
              </div>

              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}